import PropTypes from 'prop-types';
import { i18nMark } from 'lingui-react';
import { get, keys, reduce } from 'lodash';

/**
 * Map of weather codes from the API to the keys used to refer to these
 * codes within the theme colors.
 */
const weatherColors = {
  'clear-day': 'clearDay',
  'clear-night': 'clearNight',
  cloudy: 'cloudy',
  fog: 'fog',
  'partly-cloudy-day': 'partlyCloudyDay',
  'partly-cloudy-night': 'partlyCloudyNight',
  rain: 'rain',
  sleet: 'sleet',
  snow: 'snow',
  wind: 'wind',
};

/**
 * Given a "current weather" code from the API (e.g. `'clear-day'`),
 * return the path within the theme colors (e.g. `'weather.clearDay'`)
 * to the matching background color for that weather code.
 * @param {string} code
 * @return {string}
 */
export function weatherCodeToThemeColorPath(code) {
  return `weather.${get(weatherColors, code, 'clearDay')}`;
}

/**
 * Shared styled-component modifier configuration that allows the direct
 * use of API weather codes to set the background color of an element.
 */
export const modifierConfig = reduce(
  keys(weatherColors),
  (acc, key) => ({
    ...acc,
    [key]: ({ theme }) => `
      background-color: ${get(theme.colors, weatherCodeToThemeColorPath(key))};
    `,
  }),
  {},
);

const weatherColorPropTypes = reduce(
  keys(weatherColors),
  (acc, key) => ({
    ...acc,
    [weatherColors[key]]: PropTypes.string.isRequired,
  }),
  {},
);

/**
 * PropTypes for the theme parts that are required to support the colors
 * specified in the weather `modifierConfig`.
 */
export const themePropTypes = {
  colors: PropTypes.shape({
    weather: PropTypes.shape(weatherColorPropTypes).isRequired,
  }).isRequired,
};

const directionText = [
  i18nMark('N'),
  i18nMark('NNE'),
  i18nMark('NE'),
  i18nMark('ENE'),
  i18nMark('E'),
  i18nMark('ESE'),
  i18nMark('SE'),
  i18nMark('SSE'),
  i18nMark('S'),
  i18nMark('SSW'),
  i18nMark('SW'),
  i18nMark('WSW'),
  i18nMark('W'),
  i18nMark('WNW'),
  i18nMark('NW'),
  i18nMark('NNW'),
];

/**
 * Given a compass direction in degrees (0-360), return one of the sixteen
 * cardinal or intercardinal directions (N, NNE, NE, ENE, E, etc) that aligns
 * most closely with the numeric direction.
 * @param {number} num
 * @return {String}
 */
export function degToCompass(num) {
  // Since there is an angle change at every 22.5 degrees, the direction
  // should swap hands after 11.25 degrees.

  // STEPS:
  // 1. Divide the angle by 22.5 because 360deg/16 directions =
  //    22.5deg/direction change.
  // 2. Add 0.5 so that when you truncate the value you can break the 'tie'
  //    between the change threshold.
  // 3. Truncate the value using integer division (so there is no rounding).
  // 4. Directly index into the array and print the value (mod 16).
  return directionText[Math.floor(num / 22.5 + 0.5) % 16];
}

export const fahrenheitToCelsius = f => (5 / 9) * (f - 32);



// WEBPACK FOOTER //
// ./src/utils/weather.js